// ********************************************************************************************
//
//    We have modified this to file to do two things:
//
//        1) We set up video media to auto-play as soon as it is visible on the screen,
//           whether it was clicked on or swiped to
//
//        2) Added an audio toggle button to toggle the audio on/off next to video media
//           We added this because because of the auto-play feature we added for videos
//
//        3) Modified video element to set pointer events to "none" so that conext menu
//           for video controls, loops, and picture-in-picture is removed
//
//    When calling the auto-play for videos we also set up an error checking for when
//    browsers throw an error if the DOM has not been interacted with yet. In this 
//    situation, we catch the error, mute the video, and then call play function again
//
// ********************************************************************************************

if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();


        // Beginning of custom code to autoplay videos when they are visible and stop them when the are not
        //
        // Add styles to the document head
        addStyles();

        // Intersection Observer setup to auto-play videos when they are visible and stop them when they are not
        this.setupIntersectionObserver();        
      }

      // Set up intersection observer to help with auto-playing videos when they are on the screen
      setupIntersectionObserver() {
        const observerOptions = {
          root: null,
          rootMargin: '0px',
          threshold: 0.5
        };

        // Callback for the intersection observer
        const observerCallback = (entries, observer) => {
          entries.forEach(entry => {
            const mediaItem = entry.target;
            if (entry.isIntersecting) {
              // Auto-play the video when it becomes visible on the screen
              this.playActiveMedia(mediaItem);
            } else {
              // When it is no longer visible stop video and reset to beginning
              this.stopAndResetInactiveMedia(mediaItem);
            }
          });
        };

        this.observer = new IntersectionObserver(observerCallback, observerOptions);

        const mediaItems = this.elements.viewer.querySelectorAll('.product__media-item');
        mediaItems.forEach(item => {
          this.observer.observe(item);
        });

        // End of custom code        
        
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) {
          deferredMedia.loadContent(false);
        }

        // Beginning of custom code to customize how the videos are autoplayed
        //
        const videoElement = activeItem.querySelector('video');
        if (videoElement) {

          // Apply custom attributes
          videoElement.controls = false;
          videoElement.autoplay = true;
          videoElement.loop = true;
          videoElement.playsInline = true;

          // This remove the video context menu which stops users from using picture-in-picture, controls, and loops
          videoElement.style.pointerEvents = "none";

          // Play video unmuted, if it fails, mute video and call play function again
          // Updated to make sure it only mutes if the error is a "Not Allowed Error"
          videoElement.muted = false;
          videoElement.play().then(() => {
            displayAudioToggleButton(videoElement, false);
          }).catch(error => {

            if (error.name === "NotAllowedError") {
              // Fallback to playing video element muted if autoplay is not allowed
              videoElement.muted = true;
              videoElement.play().then(() => {
                displayAudioToggleButton(videoElement, true);
              }).catch(fallbackError => {
                console.error("Auto-play was prevented even when muted: ", fallbackError);
              });
            }
          });

          // Ensure video element is visible
          videoElement.style.display = 'block';
        }
      }

      // Stops and reset the active media to the beginning
      stopAndResetInactiveMedia(inactiveItem) {
        const videoElement = inactiveItem.querySelector('video');
        if (videoElement) {
          videoElement.pause();
          videoElement.currentTime = 0;
        }
      }
      //
      // End of custom code   
      

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }  

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }  
    }
  );
}

// Below are custom functions used in the custom code above
function displayAudioToggleButton(videoElement, isMuted) {

  // Find the parent container
  const videoContainer = videoElement.closest(".product-media-container");

  if (videoContainer) {

    // Ensure parent container has position relative
    videoContainer.style.position = "relative";

    // Create the audio toggle button
    let audioToggleButton = document.createElement('button');
    audioToggleButton.style.backgroundImage = `url(${createAudioIcon(videoElement.muted)})`; // Initial icon and state based on state when created
    audioToggleButton.classList.add('audio-toggle-button'); // Add custom class for styling

    // Add event listener to toggle audio
    audioToggleButton.addEventListener('click', () => {
      videoElement.muted = !videoElement.muted;
      audioToggleButton.style.backgroundImage = `url(${createAudioIcon(videoElement.muted)})`; // Update button icon
    });

    // Insert the button into the correct parent container
    videoContainer.appendChild(audioToggleButton);
  } else {
    console.error("Video container not found.");
  }
}

function addStyles() {
  const style = document.createElement('style');
  style.innerHTML = `
    .audio-toggle-button {
      position: absolute;
      top: 50%; /* Position at the bottom */
      right: -5%; /* Center horizontally */
      transform: translateX(-50%); /* Adjust position to center */
      width: 26px;
      height: 26px;
      padding: 0;
      background-color:#6A31E3;
      background-size: contain;
      background-repeat: no-repeat;
      border: none;
      border-radius: 6px;
      cursor: pointer;
      z-index: 9999; /* Ensure it's on top */
      transition: all 0.3s ease; /* Smooth transition for hover effects */
    }

    .audio-toggle-button:hover {
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
      transform: translateX(-50%) scale(1.2);
    }

    .product-media-container {
      position: relative;
      z-index: 1; /* Ensure it has a lower z-index than the button */
    }

    .product-media-container video {
      width: 100%;
      height: auto;
      display: block;
      z-index: 1;
    }

    .deferred-media__poster {
      display: none !important; /* Ensure the poster image is not blocking the video */
    }
  `;
  document.head.appendChild(style);
}

function createAudioIcon(isMuted) {
  const canvas = document.createElement('canvas');
  canvas.width = 28;
  canvas.height = 28;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = '#ffffff';

  // Draw the speaker
  ctx.beginPath();
  ctx.moveTo(5, 11);
  ctx.lineTo(9, 11);
  ctx.lineTo(13, 7);
  ctx.lineTo(13, 21);
  ctx.lineTo(9, 17);
  ctx.lineTo(5, 17);
  ctx.closePath();
  ctx.fill();

  if (isMuted) {
    // Draw the mute icon (a line through the speaker)
    ctx.beginPath();
    ctx.moveTo(5, 7);
    ctx.lineTo(19, 21);
    ctx.strokeStyle = '#ff0000';
    ctx.lineWidth = 2;
    ctx.stroke();
  } else {
    // Draw sound waves
    ctx.beginPath();
    ctx.arc(15, 14, 3, -0.5 * Math.PI, 0.5 * Math.PI);
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(17, 14, 6, -0.5 * Math.PI, 0.5 * Math.PI);
    ctx.stroke();
  }

  return canvas.toDataURL();
}

